import type { Player, SkillId } from "../types";

/** Canon 11 §6 / 04: skill level cap. */
export const MAX_SKILL_LEVEL = 3;

/** Canon 11 §6: full mastery (15 levels) costs 15 points -> 1 point per level. */
export const SKILL_UPGRADE_COST = 1;

/**
 * Whether a skill is unlocked for the player's level. A skill without a
 * `minLevel` requirement is always unlocked.
 */
export function isSkillUnlocked(minLevel: number | undefined, playerLevel: number): boolean {
  return playerLevel >= (minLevel ?? 1);
}

/**
 * Whether a skill can be raised one level: it is below the cap and the player
 * has enough unspent skill points.
 */
export function canUpgradeSkill(
  skills: Record<SkillId, number>,
  skillId: SkillId,
  skillPoints: number,
): boolean {
  const current = skills[skillId] ?? 0;
  return current < MAX_SKILL_LEVEL && skillPoints >= SKILL_UPGRADE_COST;
}

/**
 * Returns a new player with the skill raised one level and one skill point
 * spent. Returns the input player unchanged when the upgrade is not allowed.
 */
export function applySkillUpgrade(player: Player, skillId: SkillId): Player {
  if (!canUpgradeSkill(player.skills, skillId, player.skillPoints)) {
    return player;
  }
  const current = player.skills[skillId] ?? 0;
  return {
    ...player,
    skills: { ...player.skills, [skillId]: current + 1 },
    skillPoints: player.skillPoints - SKILL_UPGRADE_COST,
  };
}
